// Alumni on Broadway — responsive Swiper: 3 slides desktop, 2 on mobile.
// Loops infinitely, advances one at a time, keyboard + touch + external arrows.
//
// Also handles the per-card hover swap. The two square layers
// (.alumni-frame-person, .alumni-frame-poster) trade places along two
// separate arcs — person travels via the upper path, poster via the
// lower — so they never occupy the same plane mid-swap. Z-index flips
// instantly at the midpoint when the layers are vertically separated.
//
// Using the bundled build so all modules (Navigation, Keyboard) are guaranteed
// to be registered regardless of how the webpack alias resolves package exports.

import Swiper from 'imports/swiper/swiper-bundle.esm.js'
import gsap from 'imports/gsap'

const SWAP_DURATION = 0.425  // seconds per half-rotation
const ARC_AMOUNT    = 60     // px the arc bulges up / down at its apex

// Start and end positions for both layers. Live functions so we read the
// frame's current width on every tween tick — survives resizes + Swiper
// relayout.
const personDefault = (f) => ({ top: 30, left: 0,                   size: f.offsetWidth - 30 })
const personSwapped = (f) => ({ top: 0,  left: f.offsetWidth - 112, size: 112 })
const posterDefault = (f) => ({ top: 0,  left: f.offsetWidth - 112, size: 112 })
const posterSwapped = (f) => ({ top: 30, left: 0,                   size: f.offsetWidth - 30 })

// Forward-only wheel — t never reverses, so a mouseleave fired mid-
// enter completes the rotation through the bottom arc rather than
// rewinding back over the top ("full circle", not "undo").
//
//   t = 0     default ('out')
//   t = 0.5   swapped ('in')
//   t = 1.0   default ('out')   (one full turn)
//   t = 1.5   swapped ('in')
//   ...
//
// Stack-cap: incoming events only update a `desired` flag. They never
// queue another half-rotation while one is in flight. When a half
// completes we check the latest desired state and step ONCE if needed.
// Result: hammering enter/leave can never produce more than one extra
// half-rotation past the in-flight one, no matter how many events fire.
const buildCardSwap = (card) => {
    const frame  = card.querySelector('.alumni-frame')
    const person = card.querySelector('.alumni-frame-person')
    const poster = card.querySelector('.alumni-frame-poster')
    if (!frame || !person || !poster) return null

    const state = { t: 0 }
    let target  = 0
    let active  = null
    let desired = 'out'   // latest user intent

    const apply = () => {
        const t    = ((state.t % 1) + 1) % 1
        const lerp = 1 - Math.abs(2 * t - 1)             // 0 → 1 → 0 across a full rev
        const arc  = Math.sin(2 * Math.PI * t) * ARC_AMOUNT

        const pD = personDefault(frame), pS = personSwapped(frame)
        const oD = posterDefault(frame), oS = posterSwapped(frame)

        person.style.top    = `${pD.top  + (pS.top  - pD.top)  * lerp - arc}px`
        person.style.left   = `${pD.left + (pS.left - pD.left) * lerp}px`
        person.style.width  = `${pD.size + (pS.size - pD.size) * lerp}px`
        person.style.height = person.style.width

        poster.style.top    = `${oD.top  + (oS.top  - oD.top)  * lerp + arc}px`
        poster.style.left   = `${oD.left + (oS.left - oD.left) * lerp}px`
        poster.style.width  = `${oD.size + (oS.size - oD.size) * lerp}px`
        poster.style.height = poster.style.width

        // Z swap in the "safe" middle of each half-rotation, when the
        // two layers are vertically separated by the arc.
        const swappedDepth = t >= 0.25 && t < 0.75
        person.style.zIndex = swappedDepth ? 1 : 2
        poster.style.zIndex = swappedDepth ? 2 : 1
    }

    // The state the wheel is currently *at rest* heading toward. Half
    // boundaries are: 0/1/2/… = 'out', 0.5/1.5/… = 'in'.
    const restStateOf = (n) => (Math.round(n * 2) % 2 === 0 ? 'out' : 'in')

    // Advance the wheel one half-rotation forward. Re-evaluates `desired`
    // on complete and either stops (state matches intent) or steps once
    // more — so we can never overshoot by more than one half.
    const step = () => {
        target += 0.5
        active = gsap.to(state, {
            t: target,
            duration: SWAP_DURATION,
            ease: 'power2.inOut',
            onUpdate: apply,
            onComplete: () => {
                active = null
                // Keep numbers small over long sessions.
                if (state.t >= 2) {
                    state.t -= 2
                    target -= 2
                }
                if (restStateOf(target) !== desired) step()
            },
        })
    }

    const setDesired = (next) => {
        desired = next
        // Only start a new half-rotation if nothing is currently
        // animating AND we're not already resting at the desired state.
        // Otherwise the in-flight tween's onComplete will check.
        if (!active && restStateOf(target) !== desired) step()
    }

    return {
        in:  () => setDesired('in'),
        out: () => setDesired('out'),
    }
}

const homeAlumni = {
    init() {
        document.querySelectorAll('.home-alumni').forEach((wrap) => {
            const el = wrap.querySelector('[data-home-alumni]')
            if (!el) return

            const prev = wrap.querySelector('.alumni-prev')
            const next = wrap.querySelector('.alumni-next')

            new Swiper(el, {
                slidesPerView: 2,
                slidesPerGroup: 1,
                spaceBetween: 16,
                loop: true,
                grabCursor: false, // keep it draggable, but show the default cursor (no grab hand)
                watchOverflow: true,
                speed: 250, // faster slide transition on click
                longSwipesRatio: 0.2,
                longSwipesMs: 150,
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                navigation: {
                    prevEl: prev,
                    nextEl: next,
                    disabledClass: 'swiper-button-disabled',
                },
                breakpoints: {
                    750: {
                        slidesPerView: 3,
                        slidesPerGroup: 1,
                        spaceBetween: 24,
                    },
                },
            })

            // Per-card swap. Enter/tap aims at the swapped state, leave/tap-out
            // aims back at default. The in-flight tween is retargeted (not
            // queued) so rapid toggles can't stack rotations.
            const cards = Array.from(wrap.querySelectorAll('.alumni-card'))
                .map((card) => ({ card, swap: buildCardSwap(card) }))
                .filter((c) => c.swap)

            // Hover devices (mouse): hover to swap. Touch devices: tap a card to
            // swap it forward, tap it again (or tap anywhere else) to revert.
            if (window.matchMedia('(hover: hover)').matches) {
                cards.forEach(({ card, swap }) => {
                    card.addEventListener('mouseenter', swap.in)
                    card.addEventListener('mouseleave', swap.out)
                })
            } else {
                let open = null
                const close = () => {
                    if (open) { open.swap.out(); open = null }
                }
                cards.forEach((entry) => {
                    entry.card.addEventListener('click', () => {
                        if (open === entry) { close(); return }
                        close()
                        entry.swap.in()
                        open = entry
                    })
                })
                // Tap outside the open card closes it. pointerdown (not click)
                // so it also fires for taps on inert areas on iOS.
                document.addEventListener('pointerdown', (e) => {
                    if (open && !open.card.contains(e.target)) close()
                })
            }
        })
    }
}

export default homeAlumni
